import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getKsdmConfig } from 'helpers/index';
import { useMfgProfile } from 'app/mfgProfile';
import { resetAnalytics, trackBeforeLeaving, trackNavItem, type NavItem } from 'app/analytics';
import { useHeaderPanel } from 'hooks/useHeaderPanel';
import { useCurrentUser } from 'hooks/useDashboardData';
import ReportsDropdown from './header/ReportsDropdown';
import NotificationsDropdown from './header/NotificationsDropdown';
import ReleaseNotesDropdown from './header/ReleaseNotesDropdown';
import EditProfilePopup from './header/EditProfilePopup';
import BugReportPopup from './header/BugReportPopup';
import IndependentRatingPopup from './header/IndependentRatingPopup';
import RatingFloatingIcon from './header/RatingFloatingIcon';
import { useIndependentRating } from './header/useIndependentRating';
import SizerSelectionPopup from 'pages/sizer/selection/SizerSelectionPopup';
import { getSizerSelectionController } from 'pages/sizer/selection/sizerSelectionHost';

/**
 * Global top navigation header (#ksdm-header), §3.A.
 *
 * FAITHFUL re-platform of the Backbone header.tmpl WITH_USER markup
 * (logo + nav items + LIVE Reports / Notifications / Release-Notes dropdowns +
 * user-area menu + more-actions menu). `app_name='dashboard'` drives the
 * Dashboard icon's `active` class and omits the Workflow-Manager button
 * (header.tmpl gates it `app_name != 'dashboard'`).
 *
 * Live dropdowns are wired to the real endpoints via `headerApi` (RTK Query):
 *  - Reports history + 30s unread-count poll (the ONLY HTTP poll on the board).
 *  - Notifications (50/page, infinite-scroll, mark-read, open-project).
 *  - Release notes (Recent/Upcoming tabs split by today, mark-read).
 * Only one nav-list is open at a time; a click outside closes them (useHeaderPanel).
 *
 * `noRole` adds `non-clickable` to #ksdm-header (§ role gating).
 *
 * TWO MODES, exactly as legacy (`header.js:46-47` `MODES.WITH_USER` /
 * `MODES.WITHOUT_USER`, selected at `:182` from whether a user resolved).
 * Everything above describes WITH_USER. WITHOUT_USER — no signed-in user —
 * renders `header.tmpl:249-264`'s `{{else}}` branch: the manufacturer logo and
 * NOTHING else. No nav icons, no Reports/Notifications/Release-Notes
 * dropdowns, no user menu, no more-actions menu, no sizer/rating popups —
 * every one of those is an authenticated action, and the whole block sits
 * inside the single `{{#if user}}` guard at `header.tmpl:4`.
 *
 * This matters on exactly one route today. Every other page that renders this
 * header is served by `ReactAppAuthView`, which bounces anonymous users to
 * `/login/` before React boots, so they can only ever be WITH_USER. The CAD
 * viewer (`/model/view/`) is the sole page served by a view that deliberately
 * ALLOWS anonymous visitors — share links (`cad/react_view.py`) — so it is the
 * only place the WITHOUT_USER branch is reachable. Without it, a share-link
 * visitor got the full header, whose dropdowns fire their queries on mount;
 * those 401 and `app/services/apiConfig.ts:38-40` sends the browser to
 * `/login/`, i.e. the shared model is unviewable by the person it was shared
 * with.
 *
 * Legacy ran the WITHOUT_USER branch on this exact page, so this is parity and
 * not a new mode: `cad/templates/view.html:21` is `<body
 * data-user_id="{{user.id}}">`, which renders the literal `None` for an
 * AnonymousUser; `cad/mvc/fn/map.js` does not define `current.user`, so it
 * comes from `universal.fn.map`, whose `universal_map.js:116-140` RESOLVES
 * with no value on `'None'` (and on a missing id) rather than rejecting; and
 * `cad/mvc/fn/map.js:457-470 create.view.header` therefore constructs
 * `ViewHeader` with `user` undefined — `header.js:182`'s WITHOUT_USER. The
 * per-app `current.user` copies that DO reject on a missing id
 * (`sizer/mvc/fn/map.js:83-103`, `partners/.../map.js:89`) belong to pages
 * that are login-gated server-side anyway.
 *
 * `appName` mirrors header.tmpl's `app_name` gating (Phase 3 reuse):
 *  - 'project' uses the `project-ksdm-header` class (projectBase.html shell),
 *    drops `active` from the Dashboard icon (so it opens in a new tab via the
 *    openPage handler) and renders `#workflow_popup_button` (header.tmpl shows
 *    it for all apps except dashboard/report/cms/sizer/partners/user_settings).
 *    `onWorkflowClick` wires the Workflow Manager popup when its sub-phase lands.
 *  - 'ahu' (Phase 6a): the ahuBase.html shell renders the header element with
 *    ONLY `conected-to-error-log` (the error-resolution global-lock class,
 *    PHASE6 parity 5/186) — no project/whiteboard variant class. Like
 *    'project' it keeps the Dashboard icon inactive and shows
 *    #workflow_popup_button (header.tmpl gating).
 *  - 'report' (Phase 5a): reportBase.html:14 shell class
 *    `report-board-ksdm-header`; Dashboard icon inactive; NO
 *    #workflow_popup_button (header.tmpl:33-38 excludes 'report').
 *  - 'sizer' (Phase 4a): sizerBase.html:13 renders `<header class="">` — an
 *    EMPTY class, no shell variant (confirmed in the live DOM dump
 *    `.runtime/p4/dom-load.html`). NO #workflow_popup_button (header.tmpl:36
 *    excludes 'sizer'), and the AHU Sizer nav icon swaps to its own-app
 *    variant (header.tmpl:69-71): `ka-has-tooltip non-clickable` with a
 *    `tooltip` ATTRIBUTE and no inner tooltip markup.
 *  - 'user_settings' (Phase U-a): `userSettingsBase.html:17` renders
 *    `<header id="ksdm-header" class="">` — the same EMPTY shell class as the
 *    sizer and partners. ★ `app_name='user_settings'` appears in EXACTLY ONE
 *    condition in the whole 257-line header.tmpl: line 38's
 *    `{{#ifCompare app_name '!=' 'user_settings'}}` wrapping
 *    `#workflow_popup_button`. Hiding the Workflow Manager icon is therefore
 *    its ONLY effect — everything else renders in its default, non-active
 *    state (the AHU Sizer icon keeps the rich tooltip variant, the Dashboard
 *    icon is inactive, and NO nav item is marked `active` on this page).
 *  - 'cms' (Phase 7 shell): `CmsBase.html:16` renders
 *    `<header id="ksdm-header" class="container-fluid">` — its OWN shell class,
 *    not the empty one the sizer/partners/user-settings pages use. Two gates,
 *    both measured on the live `/admin/` page 2026-08-05:
 *      · header.tmpl:6 — the logo link gets **`target="_blank"`**, because CMS
 *        Admin is itself opened in a new tab. ★ `cms` is the ONLY appName that
 *        does this; every other page links the logo in-place.
 *      · header.tmpl:35 — `#workflow_popup_button` is suppressed.
 *    Nothing else: no nav item is `active`, and the AHU Sizer icon keeps the
 *    default rich-tooltip variant. `header.tmpl:206`'s `{{#if user.is_cms_admin}}`
 *    guards a COMMENTED-OUT Admin submenu and is inert on every page.
 *  - 'cad': the `/model/view/` 3D viewer (CadViewerPage) — no legacy shell to
 *    match (that page's header is client-rendered and broken on dev3, §C.2),
 *    so it gets the same EMPTY shell class as sizer/partners/user_settings/
 *    kai-select. Deliberately its OWN appName rather than reusing 'ahu':
 *    the viewer is a standalone read-only page with no workflow/project or
 *    AHU-editing state, so both Workflow Manager and Psychrometrics (which
 *    'ahu' would render inert, unwired) are suppressed outright instead.
 */
interface GlobalHeaderProps {
  noRole?: boolean;
  appName?:
    | 'dashboard'
    | 'project'
    | 'ahu'
    | 'report'
    | 'sizer'
    | 'partners'
    | 'user_settings'
    | 'cms'
    | 'kai-select'
    | 'cad';
  onWorkflowClick?: () => void;
  /**
   * Dev-sync J1 — `#psychrometric_chart_button` (header.tmpl:120-131), the
   * psychrometric chart launcher. AHU-only: the legacy markup is wrapped in
   * `{{#ifCompare app_name '==' 'ahu'}}`, so the button renders solely for
   * `appName='ahu'` and the AHU board passes the handler
   * (header.js:61 `click #psychrometric_chart_button` →
   * `openPsychrometricChart` :555). The popup itself is hosted by
   * AhuBoardPage (like the Workflow popup) so non-AHU pages never load the
   * chart code.
   */
  onPsychrometricsClick?: () => void;
  /**
   * The button ships `non-clickable` (header.tmpl:121) and is enabled by the
   * unit's validation-complete hook (`unit.js:779 deActivateValidating` — the
   * same hook that enables `#delete_section`), never re-disabled after.
   * usePsychrometrics supplies the React latch.
   */
  psychrometricsEnabled?: boolean;
}

const GlobalHeader = ({
  noRole = false,
  appName = 'dashboard',
  onWorkflowClick,
  onPsychrometricsClick,
  psychrometricsEnabled = false,
}: GlobalHeaderProps) => {
  const cfg = getKsdmConfig();
  const user = (cfg?.user ?? null) as
    | { initials?: string; avatar?: string | null; is_cms_admin?: boolean }
    | null;
  // header.js:182 `self.mode = self.user ? WITH_USER : WITHOUT_USER`. The
  // bootstrap config's `user` is React's `body[data-user_id]`: ReactAppView
  // serializes it to `null` for an AnonymousUser (react_views.py `_user_json`),
  // which is the same signal legacy read off the body tag.
  const signedIn = user !== null;
  // mfg logo/initials come from __KSDM_CONFIG__.mfg (injected by ReactAppView).
  // ★ Phase 7 · T2 — read them through `app/mfgProfile`, not straight off the
  // config. The CMS Admin Manufacturer screen CHANGES both at runtime; legacy
  // does it by reaching into THIS header's DOM (`$('#logo-img img').attr('src',…)`
  // and `$('#manufacturer-initials').html(value)` — `view/mfg.js:145-155,180-182`,
  // both measured on `:8130`). The store owns those two values and publishes
  // changes, so the write stays one-directional (screen → store → header) and
  // nothing outside this component touches header DOM. On every route but
  // `/admin/` the store just holds the boot-config value, unchanged.
  const { logo: mfgLogo, initials: mfgInitials } = useMfgProfile();
  // Live user (RTK 'User' tag) so the initials/avatar refresh after the
  // edit-profile popup saves or uploads an avatar (legacy header.js patches
  // #user-login-img / #user-initials directly); the server-injected bootstrap
  // config covers the first paint before /users/{id} resolves.
  const { data: apiUser } = useCurrentUser();
  const userInitials = apiUser?.initials ?? user?.initials ?? '';
  const userAvatar = apiUser ? apiUser.avatar ?? null : user?.avatar ?? null;
  const isCmsAdmin = !!(apiUser?.is_cms_admin ?? user?.is_cms_admin);

  const { openPanel, toggle } = useHeaderPanel();

  // ---- Bootstrap-style dropdowns (user menu + more-actions): open on click,
  //      close on outside click or blur (header.js `hideDropdown`). ----
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);

  // Edit-profile popup (QA #8) — header.js:546-552 openEditProfilePopup appends
  // the Backbone view to <body>; the portal below does the same. Mounted only
  // while open so it re-initializes fresh each time (`new ViewEditProfilePopup`).
  const [profileOpen, setProfileOpen] = useState(false);

  // Report a Bug popup — NOT a legacy port; there was no bug-report form in the
  // Backbone header. It lives here because the header is the one piece of chrome
  // every SPA page renders, so one entry gives every page the form. Mounted only
  // while open, like the edit-profile popup, so each report starts from a blank
  // form rather than the last one's leftovers.
  const [bugReportOpen, setBugReportOpen] = useState(false);

  // Ratings / "Independent Rating" popup (Phase 6.5) — header.js:56-58 binds
  // BOTH `#component_selector_button` and `#sizer_popup #rating_floating-icon`
  // to `openComponentSelectorPopup` (header.js:522). The header is shared, so
  // wiring it here lights the button on EVERY React page (dashboard, project
  // board, AHU board, reports) — exactly like the single Backbone header did.
  const rating = useIndependentRating();

  useEffect(() => {
    if (!userMenuOpen && !moreMenuOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (userMenuRef.current && !userMenuRef.current.contains(t)) setUserMenuOpen(false);
      if (moreMenuRef.current && !moreMenuRef.current.contains(t)) setMoreMenuOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [userMenuOpen, moreMenuOpen]);

  // Nav link open (header.js openPage): a non-active .header-navbar > a[href]
  // opens in a new tab (preventDefault + window.open). The `.active` link is
  // INERT — header.js:66 `eventStop` preventDefaults ALL navbar anchors and
  // openPage (header.js:373-374) early-returns on `.active`, so clicking the
  // current app's icon neither navigates nor reloads (QA #5).
  // `item` is the nav_item_clicked name, passed by each anchor.
  const onNavLink = useCallback((e: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => {
    const a = e.currentTarget;
    if (a.classList.contains('active')) {
      e.preventDefault();
      return;
    }
    const href = a.getAttribute('href');
    if (!href) return;
    e.preventDefault();
    e.stopPropagation();
    window.open(href, '_blank', 'noreferrer');
    trackNavItem(item);
  }, []);

  // The shell class is a property of the PAGE, not of the session, so both
  // modes wear it — legacy's is on the server-rendered `<header>` element the
  // header view only ever fills in (`sizerBase.html:13` et al), which the
  // `{{#if user}}` branch inside the template cannot reach.
  const headerClassName = `${
    appName === 'project'
      ? 'project-ksdm-header'
      : appName === 'ahu'
        ? 'conected-to-error-log'
        : appName === 'report'
          ? 'report-board-ksdm-header'
          : appName === 'cms'
            ? 'container-fluid' // CmsBase.html:16 — its own shell class
            : appName === 'sizer' ||
                appName === 'partners' ||
                appName === 'user_settings' ||
                appName === 'kai-select' ||
                appName === 'cad'
              ? '' // sizerBase.html:13 / partnersBase.html:13 / userSettingsBase.html:17 — `<header class="" id="ksdm-header">`. kai-select/cad have no legacy shell to match — same empty-class treatment as the other standalone-tool pages.
              : 'whiteboard-ksdm-header'
  }${noRole ? ' non-clickable' : ''}`;

  // ---- WITHOUT_USER (header.tmpl:249-264, the `{{#if user}}` `{{else}}`) ----
  // Logo only: NOT wrapped in the `/dashboard` anchor the WITH_USER branch uses
  // (:250-255 renders a bare `<img>`), and no `#logo-img` id. `#sizer_popup`
  // sits OUTSIDE the guard (:267) so it is still emitted, but empty — the
  // Ratings floating icon is appended by `header.js:592 renderFloatingIcon`,
  // whose only callers (:214, :240, :247) are inside the `mode == WITH_USER`
  // block, and the AHU selection popup is gated by that same block
  // (header.js:203). mfgLogo is null for an
  // anonymous visitor (`_mfg_json` returns null too), so this is the Kinetix
  // fallback in practice; it is read through the store anyway to keep the one
  // code path for the logo.
  if (!signedIn) {
    return (
      <header id="ksdm-header" className={headerClassName}>
        <div className="col-xs-12">
          <div className="">
            <div className="logo col-xs-2">
              <img src={mfgLogo || '/static/images/kinetix_logo.png'} alt="Logo" tabIndex={-1} />
            </div>
          </div>
        </div>
        <div id="sizer_popup" />
      </header>
    );
  }

  return (
    <header id="ksdm-header" className={headerClassName}>
      <div className="col-xs-12">
        <div className="">
          <div className="logo col-xs-2">
            {/* header.tmpl:6-10 — `{{#ifCompare app_name '!=' 'cms'}}` picks
                between an in-place link and `target="_blank"`. CMS Admin is the
                ONLY page on the `_blank` branch (measured on /admin/). */}
            <a
              id="logo-img"
              href="/dashboard"
              target={appName === 'cms' ? '_blank' : undefined}
              rel={appName === 'cms' ? 'noreferrer' : undefined}
              tabIndex={-1}
            >
              <img src={mfgLogo || '/static/images/kinetix_logo.png'} alt="Logo" tabIndex={-1} />
            </a>
          </div>
          <div className="navbar header-navbar">
            <a
              href="/dashboard"
              className={`${
                appName === 'dashboard' ? 'active ' : ''
              }icon-dashboard ka-has-tooltip with-padding-2`}
              tooltip="Dashboard"
              tabIndex={-1}
              onClick={(e) => onNavLink(e, 'dashboard')}
            />{' '}

            {/* Reports — live history dropdown + 30s unread-count poll */}
            <ReportsDropdown
              open={openPanel === 'reports'}
              onToggle={() => {
                if (openPanel !== 'reports') trackNavItem('reports_list');
                toggle('reports');
              }}
            />{' '}

            {/* Workflow Manager (header.tmpl:33-38 gating — hidden for
                dashboard, report, cms, sizer, partners, user_settings,
                kai-select AND cad — none of these have a workflow/project
                context; the CAD viewer has no onWorkflowClick handler to
                wire it to) */}
            {appName !== 'dashboard' &&
              appName !== 'report' &&
              appName !== 'cms' &&
              appName !== 'sizer' &&
              appName !== 'partners' &&
              appName !== 'user_settings' &&
              appName !== 'kai-select' &&
              appName !== 'cad' && (
              /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
              <a
                id="workflow_popup_button"
                className="icon-workflow"
                tabIndex={-1}
                onClick={() => {
                  onWorkflowClick?.();
                  trackNavItem('workflow');
                }}
              >
                <div className="tooltip_with_header workflow_manager">
                  <div className="tooltip_header">Workflow Manager</div>
                  <div className="tooltip_body">
                    <div className="pb-0">Start and Complete various Tasks</div>
                    <div>Design, Budget, Bid, Order, Submittals, ...</div>
                  </div>
                </div>
              </a>
            )}{' '}

            {/* AHU Sizer — header.tmpl:69-77 renders TWO variants: on the
                sizer itself an inert `ka-has-tooltip non-clickable` icon with
                a plain `tooltip` attribute (:70), elsewhere the rich
                tooltip_with_header block (:73-80). */}
            {appName === 'sizer' ? (
              <a
                href="/sizer"
                target="blank"
                rel="noreferrer"
                id="sizer_button"
                className="icon-sizer-1 ka-has-tooltip non-clickable with-padding-2"
                tooltip="AHU Sizer"
                tabIndex={-1}
                onClick={(e) => onNavLink(e, 'sizer')}
              />
            ) : (
              <a
                href="/sizer"
                target="blank"
                rel="noreferrer"
                id="sizer_button"
                className="icon-sizer-1 with-padding-2"
                tabIndex={-1}
                onClick={(e) => onNavLink(e, 'sizer')}
              >
                <div className="tooltip_with_header sizer">
                  <div className="tooltip_header">AHU Sizer</div>
                  <div className="tooltip_body">
                    <div className="">Size AHU Cabinet, Coil and Filter</div>
                  </div>
                </div>
              </a>
            )}{' '}

            {/* Partners */}
            <a
              href="/partners"
              id="partners_button"
              target="blank"
              rel="noreferrer"
              /* header.tmpl:82 — `active` when app_name == 'partners' */
              className={`icon-partners right-border${
                appName === 'partners' ? ' active' : ''
              } with-padding-2`}
              tabIndex={-1}
              onClick={(e) => onNavLink(e, 'partners')}
            >
              <div className="tooltip_with_header partners">
                <div className="tooltip_header">Partners</div>
                <div className="tooltip_body">
                  <div className="pb-0">Manage; Add, Edit, Delete Contacts</div>
                  <div>Consulting engineers, contractors, owners</div>
                </div>
              </div>
            </a>{' '}
            <div className="right-border-wrapper">
              <div />
            </div>{' '}

            {/* Ratings / component selector. Markup + tooltip stay byte-identical
                to header.tmpl:100-109; the ONLY change is the onClick that was
                missing (the Phase 6.5 regression). No role gate beyond the
                app-wide `noRole` → #ksdm-header.non-clickable (header.tmpl:100
                sits inside the single {{#if user}} guard with no role check). */}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              id="component_selector_button"
              className=""
              tabIndex={-1}
              onClick={() => {
                rating.open();
                trackNavItem('ratings');
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 -960 960 960"
                width="24"
              >
                <path
                  fill="#767676"
                  d="m482-200 114-113-114-113-42 42 43 43q-28 1-54.5-9T381-381q-20-20-30.5-46T340-479q0-17 4.5-34t12.5-33l-44-44q-17 25-25 53t-8 57q0 38 15 75t44 66q29 29 65 43.5t74 15.5l-38 38 42 42Zm165-170q17-25 25-53t8-57q0-38-14.5-75.5T622-622q-29-29-65.5-43T482-679l38-39-42-42-114 113 114 113 42-42-44-44q27 0 55 10.5t48 30.5q20 20 30.5 46t10.5 52q0 17-4.5 34T603-414l44 44ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"
                />
              </svg>
              <div className="tooltip_with_header ratings">
                <div className="tooltip_header">Ratings</div>
                <div className="tooltip_body">
                  <div className="pb-0">Rate various Components</div>
                  <div>Fans, Coils, Gas Heaters, Rotary Heat-Ex, Plate Heat-Ex, ...</div>
                </div>
              </div>
            </a>{' '}

            {/* Kalculator */}
            <a
              id="kl_btn"
              className="icon-kalculator with-padding-2"
              tabIndex={-1}
              onClick={() => {
                window.open(
                  '/static/kalculators/',
                  'Kalculator',
                  'width=325, height=525, left=130, location=no, menubar=no, top=70, resizable=no'
                );
                // The Kalculator is a standalone page without PostHog, so
                // opening it is all we can see.
                trackNavItem('kalculator');
              }}
            >
              <div className="tooltip_with_header kalculator">
                <div className="tooltip_header">Kalculator</div>
                <div className="tooltip_body">
                  <div className="pb-0">Perform useful Calculations</div>
                  <div>Fan Laws, Evaporative Cooling Load, Electrical Load,...</div>
                </div>
              </div>
            </a>{' '}

            {/* Psychrometric chart (Dev-sync J1) — header.tmpl:120-131,
                gated {{#ifCompare app_name '==' 'ahu'}}. Sits between the
                Kalculator and Release Notes exactly as in the template.
                Starts non-clickable until the unit's first validation
                completes (unit.js:779). Icon + hover CSS already merged
                (header.css:728-730/:797-800). */}
            {appName === 'ahu' && (
              /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
              <a
                id="psychrometric_chart_button"
                className={`with-padding-2${psychrometricsEnabled ? '' : ' non-clickable'}`}
                tabIndex={-1}
                onClick={() => {
                  onPsychrometricsClick?.();
                  // Counted only once it is clickable (non-clickable until the
                  // unit's first validation).
                  if (psychrometricsEnabled) trackNavItem('psychrometrics');
                }}
              >
                <i className="icon-psychrometric-chart" />
                <div className="tooltip_with_header psychrometric_chart">
                  <div className="tooltip_header">Psychrometrics</div>
                  <div className="tooltip_body">
                    <div className="">View and synchronize air conditions across the AHU</div>
                  </div>
                </div>
              </a>
            )}{' '}

            {/* Release Notes — live dropdown (Recent / Upcoming tabs) */}
            <ReleaseNotesDropdown
              open={openPanel === 'release_notes'}
              onToggle={() => {
                if (openPanel !== 'release_notes') trackNavItem('release_notes');
                toggle('release_notes');
              }}
            />{' '}

            {/* Notifications — live dropdown (50/page, infinite scroll) */}
            <NotificationsDropdown
              open={openPanel === 'notifications'}
              onToggle={() => {
                if (openPanel !== 'notifications') trackNavItem('notifications');
                toggle('notifications');
              }}
            />{' '}
            <div className="right-border-wrapper">
              <div />
            </div>{' '}

            {/* KAI Select — the in-repo React port at /kai-select/ (#1259).
                The S3-hosted widget at /app/kai-select was retired 2026-10-11. */}
            <a
              href="/kai-select/"
              id="kai_select"
              target="blank"
              rel="noreferrer"
              className="icon-ai-techno right-border with-padding-2"
              tabIndex={-1}
              onClick={(e) => onNavLink(e, 'kai_select')}
            >
              <div className="tooltip_with_header kai_select">
                <div className="tooltip_header">KAI Select</div>
                <div className="tooltip_body">
                  <div>AI Driven Selection of Air Handling Units</div>
                </div>
              </div>
            </a>{' '}
            <div className="right-border-wrapper">
              <div />
            </div>{' '}

            {/* User menu (avatar + initials + caret) */}
            <div
              id="navbar-user-login"
              className={`dropdown show top-9${userMenuOpen ? ' open' : ''}`}
              ref={userMenuRef}
            >
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                className={`dropdown-btn dropdown-toggle${userMenuOpen ? ' active' : ''}`}
                data-toggle="dropdown"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setMoreMenuOpen(false);
                  setUserMenuOpen((o) => !o);
                }}
              >
                <span id="user-login-img">
                  {userAvatar ? (
                    <>
                      <img src={userAvatar} alt="User Image" />
                      <i />
                    </>
                  ) : (
                    <i className="icon-account" />
                  )}
                </span>{' '}
                {/* Whitespace parity (QA #1): header.tmpl:155-168 renders these
                    inline spans with newlines between them, which collapse to a
                    single space each (icon⎵KSL⎵:⎵VDA⎵caret). JSX strips
                    inter-element whitespace, so restore the text nodes. */}
                <span id="manufacturer-initials">{mfgInitials} :</span>{' '}
                <span id="user-initials">{userInitials}</span>{' '}
                <i className="user-icon icon-arrow-down-1" />
              </a>
              <ul className="dropdown-menu" style={userMenuOpen ? { display: 'block' } : undefined}>
                <li className="menu-header">
                  <a tabIndex={-1}>User Menu</a>
                </li>
                <li
                  id="edit_profile_button"
                  tabIndex={-1}
                  onClick={() => {
                    // Bootstrap closes the dropdown on any document click;
                    // header.js:60 then opens the popup.
                    setUserMenuOpen(false);
                    setProfileOpen(true);
                  }}
                >
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a tabIndex={-1}>User profile</a>
                </li>
                <li id="user_defined_settings_button" tabIndex={-1}>
                  <a
                    href="/user_settings/#submittal"
                    target="_blank"
                    rel="noreferrer"
                    tabIndex={-1}
                    onClick={() => trackNavItem('user_settings')}
                  >
                    User Settings
                  </a>
                </li>
                <li>
                  <a
                    href="/logout/"
                    tabIndex={-1}
                    onClick={() => {
                      // Before the reset, so it's still attributed to the user.
                      trackBeforeLeaving('logged_out');
                      resetAnalytics();
                    }}
                  >
                    Sign out
                  </a>
                </li>
              </ul>
            </div>{' '}

            {/* More Actions overflow */}
            <div
              className={`dropdown show to-left top-9 more-actions-show${
                moreMenuOpen ? ' open' : ''
              }`}
              ref={moreMenuRef}
            >
              <button
                className={`more-actions-dropdown dropdown-btn icon-more-actions ka-has-tooltip dropdown-toggle${
                  moreMenuOpen ? ' active' : ''
                }`}
                type="button"
                tooltip="More Actions"
                data-toggle="dropdown"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(false);
                  setMoreMenuOpen((o) => !o);
                }}
              />
              <ul
                className={`dropdown-menu more-actions-dropdown-menu${
                  moreMenuOpen ? ' active' : ''
                }`}
                style={moreMenuOpen ? { display: 'block' } : undefined}
              >
                <li className="menu-header">
                  <a tabIndex={-1}>More Actions</a>
                </li>
                {/* First in the list on purpose: the only ACTION among four
                    document links, and the one someone hunts for while already
                    annoyed. Closes the dropdown before opening the popup, the
                    same two-step the user menu's Edit Profile item uses (the
                    Bootstrap dropdown would otherwise stay open behind it). */}
                <li
                  id="report_bug_button"
                  tabIndex={-1}
                  onClick={() => {
                    setMoreMenuOpen(false);
                    setBugReportOpen(true);
                    trackNavItem('report_bug');
                  }}
                >
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a tabIndex={-1}>Report a Bug</a>
                </li>
                <li>
                  <a
                    href="/guide"
                    target="_blank"
                    rel="noreferrer"
                    tabIndex={-1}
                    onClick={() => trackNavItem('user_guide')}
                  >
                    User Guide
                  </a>
                </li>
                <li>
                  <a
                    href="/step_by_step"
                    target="_blank"
                    rel="noreferrer"
                    tabIndex={-1}
                    onClick={() => trackNavItem('step_by_step')}
                  >
                    Step By Step
                  </a>
                </li>
                <li>
                  <a href="/terms_of_use" target="_blank" rel="noreferrer" tabIndex={-1}>
                    Terms of Use
                  </a>
                </li>
                <li>
                  <a href="/privacy_policy" target="_blank" rel="noreferrer" tabIndex={-1}>
                    Privacy Policy
                  </a>
                </li>
                {isCmsAdmin && (
                  <li>
                    <a className="dropdown-item" href="#">
                      Admin{' '}
                    </a>
                    <ul className="dropdown-menu dropdown-submenu">
                      <li>
                        <a
                          className="dropdown-item"
                          href="/admin"
                          target="_blank"
                          rel="noreferrer"
                          tabIndex={-1}
                        >
                          CMS
                        </a>
                      </li>
                      <li>
                        <a
                          className="dropdown-item"
                          href="/start_up_guide"
                          target="_blank"
                          rel="noreferrer"
                          tabIndex={-1}
                        >
                          Deployment Plan
                        </a>
                      </li>
                    </ul>
                  </li>
                )}
              </ul>
              {/* header.tmpl:243-245 breaks the line between the dropdown `</ul>`
                  and this label, so legacy renders a word-space between the
                  kebab button and the (zero-width) label. Both are inline-block,
                  so that space makes `.more-actions-show` 25px wide instead of
                  21px — and since `.navbar` is `float:right`, dropping it made
                  the navbar 4px narrower and pushed EVERY top-right icon 4px
                  right (measured dev 667px / dev3 663px). */}{' '}
              <label id="css-toggle-menu" />
            </div>
          </div>
        </div>
      </div>
      {/* header.js:592 renderFloatingIcon appends `#rating_floating-icon`
          into `#sizer_popup` on EVERY page load and toggles `.hide` from the
          stored state — so the node always exists.
          ★ 4e — `on-sizer-page` is added by the SELECTION POPUP's `initialize`
          (`header/view/sizer_popup.js:147`, `window.location.pathname ===
          '/sizer/'`), and since the header builds that popup on every page the
          class is present on legacy `/sizer/` from load, whatever state the
          popup is in. It only styles `.floating-icon` descendants
          (`_sizer.content.scss:397-405`) — including the Ratings icon, which on
          `/sizer/` therefore sits at `right:256px; top:calc(100% - 60px)` rather
          than its default `right:36px`. `appName` is the React equivalent of the
          pathname test.
          ★ The matching `on-ahu-page` branch (sizer_popup.js:148,
          `pathname.includes('/ahu/detail/')`) is now wired too — it was the 4e
          gap noted here. `.on-ahu-page .floating-icon` moves both icons to
          `right:85px !important; top:calc(100% - 85px)` and the Ratings icon on
          to `right:130px !important` (common.css:11340-11346); that is what puts
          Component Ratings to the LEFT of the palette on legacy's AHU board.
          Without the class both fell back to the default `right:36px;
          top:calc(100% - 65px)`, so Ratings rendered UNDER the palette instead.
          `appName === 'ahu'` is the React equivalent: only AhuBoardPage passes
          it and its sole route is `/ahu/detail/:ahuId` (App.tsx:37). */}
      <div
        id="sizer_popup"
        className={
          appName === 'sizer'
            ? 'on-sizer-page'
            : appName === 'ahu'
              ? 'on-ahu-page'
              : undefined
        }
      >
        {/* ★ THE AHU SELECTION POPUP, on every board — `header.js:203-215`.
            Legacy builds `sizer_popup` in the header for every page with a
            logged-in user, which is what lets a selection made on `/sizer/`
            open, minimize and restore on the AHU board, the dashboard and the
            project board. 4e scoped it to `/sizer/` and passed the markup in
            through a slot; QA reported the three symptoms of that narrowing
            (no window on the other boards, no floating-icon restore there, and
            no reaction to a `/sizer/` tab's APPLY), so it is header-owned again.
            The controller is the SPA-wide singleton — see `sizerSelectionHost`.

            `noRole` is the React `mode != MODES.WITH_USER` (header.js:203): no
            user, no popup.

            Rendered BEFORE `<RatingFloatingIcon>` because that is legacy's
            order: `minimize()` replaces `#sizer_popup`'s contents with the sizer
            floating icon (`sizer_popup.js:743`) and then calls back into
            `renderFloatingIcon()`, which APPENDS the Ratings icon after it
            (`header.js:592`) — the `.floating-icon + #rating_floating-icon`
            sibling shift in `_sizer.content.scss:397-405` depends on it. */}
        {!noRole && <SizerSelectionPopup controller={getSizerSelectionController()} />}
        <RatingFloatingIcon visible={rating.iconVisible} onOpen={rating.open} />
      </div>
      {profileOpen &&
        createPortal(<EditProfilePopup onClose={() => setProfileOpen(false)} />, document.body)}
      {/* Portalled to <body> for the same reason as the edit-profile popup: the
          header is `position: fixed` with its own stacking context, so a
          `.popup-wrapper` rendered inside it cannot cover the page. */}
      {bugReportOpen &&
        createPortal(
          <BugReportPopup appName={appName} onClose={() => setBugReportOpen(false)} />,
          document.body
        )}
      {/* header.js:529-530 `$("body").append(view.$el)`. Mounted on the FIRST
          open (or the auto-reopen) and then kept mounted for the rest of the page
          session — create-once/never-destroy; close/minimize only drop `.active`
          (header.js:525-528, rating_mode.scss:582-591). */}
      {rating.mounted &&
        createPortal(
          <IndependentRatingPopup
            active={rating.active}
            initialTab={rating.initialTab}
            onClose={rating.close}
            onMinimize={rating.minimize}
          />,
          document.body
        )}
    </header>
  );
};

export default GlobalHeader;
